import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {InfoIcon} from '@primer/octicons-react'
import {Button, Link, Truncate} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {useState} from 'react'

import {LABELS} from '../constants/labels'
import {VALUES} from '../constants/values'
import type {ReferencedEventCommitNode} from './issue-viewer-page-query-types'
import styles from './ReferencedEventInner.module.css'
import {ReferencedEventVerificationStatus} from './ReferencedEventVerificationStatus'
import type {SecondaryTimelineSource} from './secondary-timeline-source'

// RELAY-CLEANUP(ReferencedEvent): widened repository.owner so the still-Relay parent data is assignable.
// The GraphQL owner field is a union (Organization | User) with __typename discriminators in the SPQ types,
// but this component only queries `login` — so the Relay-generated owner type is `{ login: string }` without
// __typename. Using the simpler shape avoids a type mismatch at the Relay/plain-props boundary.
// Remove when ReferencedEvent leaves Relay.
export type ReferencedEventInnerFragment = RelayCompat<
  Pick<ReferencedEventCommitNode, 'message' | 'messageHeadlineHTML' | 'messageBodyHTML' | 'url' | 'abbreviatedOid'> & {
    readonly repository?: {
      readonly name: string
      readonly owner: {readonly login: string}
      readonly defaultBranch: string
    } | null
  }
>

export type ReferencedEventInnerProps = {
  commit: ReferencedEventInnerFragment
  viewerLogin: string | null
  willCloseSubject: boolean
  subjectType: string
  eventId: string
  secondaryTimelineSource?: SecondaryTimelineSource
}

export function ReferencedEventInner({
  commit,
  willCloseSubject,
  viewerLogin,
  eventId,
  subjectType,
  secondaryTimelineSource,
}: ReferencedEventInnerProps): React.ReactElement {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const [showCommitBody, setShowCommitBody] = useState(false)

  // Guard against missing repository data (can happen with deleted/inaccessible repos)
  const repository = commit.repository
  const defaultBranch = repository?.defaultBranch ?? ''
  const willCloseMessage = defaultBranch
    ? LABELS.commitWillCloseMessage(subjectType, commit.abbreviatedOid, defaultBranch)
    : ''

  const hovercardAttributes = repository
    ? getHovercardAttributesForType('commit', {
        owner: repository.owner.login,
        repo: repository.name,
        commitish: commit.abbreviatedOid,
      })
    : undefined

  return (
    <div className={styles.referencedCommitContainer}>
      <div className={styles.commitHeaderRow}>
        <div className={styles.commitMetadata}>
          <Truncate title={commit.message} className={styles.commitMessageText}>
            {hovercardAttributes ? (
              <Link
                href={commit.url}
                target="_blank"
                {...hovercardAttributes}
                muted
                aria-label={commit.message}
                className={styles.commitHashLink}
              >
                <MarkdownViewer
                  verifiedHTML={
                    // we can be sure this is a SafeHTMLString because the concatenated content comes from a trusted
                    // GitHub graphql query
                    // eslint-disable-next-line github/unescaped-html-literal, @github-ui/github-monorepo/no-cast-as-safe-html-string -- Composed from sanitized segments
                    `<p style="font-family:monospace;font-size:12px">${commit.messageHeadlineHTML}</p>` as SafeHTMLString
                  }
                />
              </Link>
            ) : (
              <MarkdownViewer
                verifiedHTML={
                  // we can be sure this is a SafeHTMLString because the concatenated content comes from a trusted
                  // GitHub graphql query
                  // eslint-disable-next-line github/unescaped-html-literal, @github-ui/github-monorepo/no-cast-as-safe-html-string -- Composed from sanitized segments
                  `<p style="font-family:monospace;font-size:12px">${commit.messageHeadlineHTML}</p>` as SafeHTMLString
                }
              />
            )}
          </Truncate>
          {commit.messageBodyHTML && (
            <Button
              size="small"
              onClick={() => setShowCommitBody(!showCommitBody)}
              aria-label={showCommitBody ? 'Hide commit message body' : 'Show commit message body'}
              aria-expanded={showCommitBody}
              className={styles.toggleButton}
            >
              ...
            </Button>
          )}
        </div>
        <div className={styles.commitActions}>
          {willCloseSubject && defaultBranch && (
            <Tooltip text={willCloseMessage} type="label">
              <Link href={VALUES.closingViaCommitMessageUrl} muted>
                <InfoIcon />
              </Link>
            </Tooltip>
          )}
          <ReferencedEventVerificationStatus
            secondaryTimelineSource={secondaryTimelineSource}
            eventId={eventId}
            viewerLogin={viewerLogin}
          />
          <Link href={commit.url} muted className={styles.repoNameLink}>
            {commit.abbreviatedOid}
          </Link>
        </div>
      </div>
      {showCommitBody && (
        <MarkdownViewer
          verifiedHTML={
            // we can be sure this is a SafeHTMLString because the concatenated content comes from a trusted
            // GitHub graphql query
            // eslint-disable-next-line github/unescaped-html-literal, @github-ui/github-monorepo/no-cast-as-safe-html-string -- Composed from sanitized segments
            `<p style="font-family:monospace;font-size:12px;white-space:pre-wrap">${commit.messageBodyHTML}</p>` as SafeHTMLString
          }
        />
      )}
    </div>
  )
}
